// pattern: imperative-shell

import { today } from "../core/date-utils";
import { STAGES } from "../core/observation-model";
import type {
	ApiLocation,
	ApiObservation,
	ApiParsedObservation,
	ApiPhotoId,
} from "../shell/api-client";
import { api } from "../shell/api-client";
import { navigate } from "../shell/router";
import { renderPhotoCapture } from "./photo-capture";
import { isSpeechSupported, renderVoiceButton } from "./voice-button";

interface FormState {
	locations: ApiLocation[];
	existing: ApiObservation | null; // null = new, set = editing
}

export async function renderObservationForm(
	container: HTMLElement,
	editId?: string,
): Promise<void> {
	container.innerHTML = '<div class="loading">Loading...</div>';

	try {
		const [locations, existing] = await Promise.all([
			api.getLocations(),
			editId
				? api
						.getObservations({ limit: "200" })
						.then((obs) => obs.find((o) => o.id === editId) ?? null)
				: Promise.resolve(null),
		]);

		if (editId && !existing) {
			container.innerHTML =
				'<div class="content"><div class="error">Observation not found</div></div>';
			return;
		}

		if (locations.length === 0) {
			container.innerHTML = `
				<div class="content">
					<div class="empty">
						<div class="empty-icon">\u{1F4CD}</div>
						<div class="empty-text">Add a garden location first</div>
						<button class="btn btn-primary" id="go-locations">Add Location</button>
					</div>
				</div>
			`;
			container
				.querySelector("#go-locations")
				?.addEventListener("click", () => navigate("locations"));
			return;
		}

		renderForm(container, { locations, existing });
	} catch (e) {
		container.innerHTML = `<div class="content"><div class="error">${e instanceof Error ? e.message : "unknown error"}</div></div>`;
	}
}

function renderForm(container: HTMLElement, state: FormState): void {
	const { locations, existing } = state;
	const isEdit = !!existing;

	const locationOptions = locations
		.map(
			(l) =>
				`<option value="${l.id}"${existing?.location_id === l.id ? " selected" : ""}>${esc(l.name)}</option>`,
		)
		.join("");

	const stageOptions = STAGES.map(
		(s) => `<option value="${s}"${existing?.stage === s ? " selected" : ""}>${s}</option>`,
	).join("");

	const nlSection = isEdit
		? ""
		: `
		<div class="nl-section">
			<div id="photo-container"></div>
			<div id="photo-status-msg"></div>

			<div class="nl-header">
				<span class="form-label">Quick entry</span>
				<span class="nl-hint">Speak or type naturally</span>
			</div>
			<div class="nl-input-row">
				<textarea class="form-textarea nl-textarea" id="nl-input"
					placeholder="e.g. The hostas in the shade garden are starting to bud"></textarea>
				<div id="voice-container"></div>
			</div>
			<div id="nl-interim" class="nl-interim"></div>
			<button class="btn btn-secondary btn-full" type="button" id="nl-parse-btn">
				Parse entry
			</button>
			<div id="nl-status"></div>
			<div class="nl-divider"><span>or fill in manually</span></div>
		</div>
	`;

	container.innerHTML = `
		<div class="content">
			<form id="obs-form">
				${nlSection}

				<div class="form-group">
					<label class="form-label" for="observed_date">Date</label>
					<input class="form-input" type="date" id="observed_date" name="observed_date"
						value="${existing?.observed_date ?? today()}" required />
				</div>

				<div class="form-group">
					<label class="form-label" for="plant_name">Plant</label>
					<input class="form-input" type="text" id="plant_name" name="plant_name"
						value="${existing ? esc(existing.plant_name) : ""}"
						placeholder="e.g. Hosta, Rosa rugosa" required autocomplete="off" />
				</div>

				<div class="form-group">
					<label class="form-label" for="location_id">Location</label>
					<select class="form-select" id="location_id" name="location_id" required>
						${locationOptions}
					</select>
				</div>

				<div class="form-group">
					<label class="form-label" for="stage">Stage</label>
					<select class="form-select" id="stage" name="stage" required>
						${stageOptions}
					</select>
				</div>

				<div class="form-group">
					<label class="form-label" for="notes">Notes</label>
					<textarea class="form-textarea" id="notes" name="notes"
						placeholder="Any additional details...">${existing?.notes ?? ""}</textarea>
				</div>

				<input type="hidden" id="raw_input" name="raw_input" value="" />

				<div id="form-error"></div>

				<button class="btn btn-primary btn-full" type="submit">
					${isEdit ? "Update" : "Save"} Observation
				</button>

				${
					isEdit
						? `<button class="btn btn-danger btn-full" type="button" id="delete-btn" style="margin-top: 8px">
						Delete
					</button>`
						: ""
				}
			</form>
		</div>
	`;

	// Wire up NL input + voice (new observations only)
	if (!isEdit) {
		wireNLInput(container, locations);
	}

	wireFormSubmit(container, state);
	wireDelete(container, existing);
}

function wireNLInput(container: HTMLElement, locations: ApiLocation[]): void {
	const nlInput = container.querySelector<HTMLTextAreaElement>("#nl-input")!;
	const parseBtn = container.querySelector<HTMLButtonElement>("#nl-parse-btn")!;
	const statusEl = container.querySelector("#nl-status")!;
	const interimEl = container.querySelector("#nl-interim")!;
	const voiceContainer = container.querySelector<HTMLElement>("#voice-container")!;
	const photoContainer = container.querySelector<HTMLElement>("#photo-container")!;
	const photoStatusMsg = container.querySelector<HTMLElement>("#photo-status-msg")!;

	// Photo capture
	renderPhotoCapture(
		photoContainer,
		(result: ApiPhotoId) => {
			fillFormFromPhoto(container, result);
		},
		(message: string, isError?: boolean) => {
			photoStatusMsg.innerHTML = isError
				? `<div class="error">${esc(message)}</div>`
				: `<div class="photo-status">${esc(message)}</div>`;
		},
	);

	// Voice button
	if (isSpeechSupported()) {
		renderVoiceButton(
			voiceContainer,
			(result) => {
				// Final transcript — append to text area
				const current = nlInput.value.trim();
				nlInput.value = current ? `${current} ${result.transcript}` : result.transcript;
				interimEl.textContent = "";
			},
			(interim) => {
				// Interim transcript — show live feedback
				interimEl.textContent = interim;
			},
		);
	}

	// Parse button
	parseBtn.addEventListener("click", async () => {
		const text = nlInput.value.trim();
		if (!text) {
			statusEl.innerHTML = '<div class="error">Type or speak something first</div>';
			return;
		}

		parseBtn.disabled = true;
		statusEl.innerHTML = '<div class="loading">Understanding your entry...</div>';

		try {
			const result = await api.parseNaturalLanguage(text);

			if (result.observations.length === 0) {
				statusEl.innerHTML =
					'<div class="error">Couldn\'t identify any plant observations. Try being more specific.</div>';
				parseBtn.disabled = false;
				return;
			}

			if (result.observations.length === 1) {
				// Single observation — fill the form directly
				fillFormFromParsed(container, result.observations[0]!, locations, text);
				statusEl.innerHTML = '<div class="nl-success">Fields filled — review and save below</div>';
			} else {
				// Multiple observations — show picker
				renderMultiParsed(container, statusEl as HTMLElement, result.observations, locations, text);
			}

			parseBtn.disabled = false;
		} catch (e) {
			statusEl.innerHTML = `<div class="error">${e instanceof Error ? e.message : "Parse failed"}</div>`;
			parseBtn.disabled = false;
		}
	});
}

function fillFormFromParsed(
	container: HTMLElement,
	obs: ApiParsedObservation,
	locations: ApiLocation[],
	rawInput: string,
): void {
	const form = container.querySelector<HTMLFormElement>("#obs-form")!;

	// Plant name
	const plantInput = form.querySelector<HTMLInputElement>("#plant_name")!;
	plantInput.value = obs.plant_name;

	// Date
	if (obs.observed_date) {
		const dateInput = form.querySelector<HTMLInputElement>("#observed_date")!;
		dateInput.value = obs.observed_date;
	}

	// Location — match by ID or name
	if (obs.location_id) {
		const locSelect = form.querySelector<HTMLSelectElement>("#location_id")!;
		locSelect.value = obs.location_id;
	} else if (obs.location_name) {
		// Try to match by name
		const match = locations.find((l) => l.name.toLowerCase() === obs.location_name?.toLowerCase());
		if (match) {
			const locSelect = form.querySelector<HTMLSelectElement>("#location_id")!;
			locSelect.value = match.id;
		}
	}

	// Stage
	const stageSelect = form.querySelector<HTMLSelectElement>("#stage")!;
	if (STAGES.includes(obs.stage as (typeof STAGES)[number])) {
		stageSelect.value = obs.stage;
	}

	// Notes
	if (obs.notes) {
		const notesInput = form.querySelector<HTMLTextAreaElement>("#notes")!;
		notesInput.value = obs.notes;
	}

	// Raw input
	const rawInputField = form.querySelector<HTMLInputElement>("#raw_input")!;
	rawInputField.value = rawInput;

	// Scroll to the form fields
	plantInput.scrollIntoView({ behavior: "smooth", block: "center" });
}

function fillFormFromPhoto(container: HTMLElement, result: ApiPhotoId): void {
	const form = container.querySelector<HTMLFormElement>("#obs-form")!;

	// Plant name — include variety if available
	const plantInput = form.querySelector<HTMLInputElement>("#plant_name")!;
	const plantName = result.variety ? `${result.plant_name} '${result.variety}'` : result.plant_name;
	plantInput.value = plantName;

	// Stage (if identified from a live plant photo)
	if (result.stage) {
		const stageSelect = form.querySelector<HTMLSelectElement>("#stage")!;
		if (STAGES.includes(result.stage as (typeof STAGES)[number])) {
			stageSelect.value = result.stage;
		}
	}

	// Build notes from identification details
	const noteParts: string[] = [];
	if (result.scientific_name) noteParts.push(`Scientific: ${result.scientific_name}`);
	if (result.is_seed_packet) noteParts.push("(from seed packet)");
	if (result.notes) noteParts.push(result.notes);

	if (noteParts.length > 0) {
		const notesInput = form.querySelector<HTMLTextAreaElement>("#notes")!;
		notesInput.value = noteParts.join(". ");
	}

	// Raw input
	const rawInputField = form.querySelector<HTMLInputElement>("#raw_input")!;
	rawInputField.value = `[photo identification] ${result.plant_name}`;

	// Scroll to form
	plantInput.scrollIntoView({ behavior: "smooth", block: "center" });

	// Highlight the plant name to draw attention
	plantInput.focus();
	plantInput.select();
}

function renderMultiParsed(
	container: HTMLElement,
	statusEl: HTMLElement,
	observations: ApiParsedObservation[],
	locations: ApiLocation[],
	rawInput: string,
): void {
	statusEl.innerHTML = `
		<div class="multi-parsed">
			<div class="form-label">Found ${observations.length} observations — tap one to fill the form:</div>
			${observations
				.map(
					(obs, i) => `
				<button class="card multi-parsed-card" type="button" data-parsed-idx="${i}">
					<div class="card-header">
						<span class="plant-name">${esc(obs.plant_name)}</span>
						<span class="stage-pill stage-${obs.stage}">${obs.stage}</span>
					</div>
					${obs.location_name ? `<div class="location-badge">${esc(obs.location_name)}</div>` : ""}
				</button>
			`,
				)
				.join("")}
			<div class="nl-hint" style="margin-top:8px">You can save each one separately. The others will stay here.</div>
		</div>
	`;

	statusEl.querySelectorAll<HTMLButtonElement>("[data-parsed-idx]").forEach((btn) => {
		btn.addEventListener("click", () => {
			const idx = Number(btn.dataset["parsedIdx"]);
			const obs = observations[idx]!;
			fillFormFromParsed(container, obs, locations, rawInput);

			// Mark this one as selected
			statusEl
				.querySelectorAll(".multi-parsed-card")
				.forEach((c) => c.classList.remove("multi-selected"));
			btn.classList.add("multi-selected");
		});
	});
}

function wireFormSubmit(container: HTMLElement, state: FormState): void {
	const { existing } = state;
	const isEdit = !!existing;
	const form = container.querySelector<HTMLFormElement>("#obs-form")!;

	form.addEventListener("submit", async (e) => {
		e.preventDefault();
		const fd = new FormData(form);
		const rawInput = fd.get("raw_input") as string;
		const data = {
			observed_date: fd.get("observed_date"),
			plant_name: fd.get("plant_name"),
			location_id: fd.get("location_id"),
			stage: fd.get("stage"),
			notes: fd.get("notes") || undefined,
			raw_input: rawInput || undefined,
		};

		const errEl = container.querySelector("#form-error")!;
		try {
			if (isEdit && existing) {
				await api.updateObservation(existing.id, data);
			} else {
				await api.createObservation(data);
			}
			navigate("");
		} catch (err) {
			errEl.innerHTML = `<div class="error">${err instanceof Error ? err.message : "Save failed"}</div>`;
		}
	});
}

function wireDelete(container: HTMLElement, existing: ApiObservation | null): void {
	const deleteBtn = container.querySelector<HTMLButtonElement>("#delete-btn");
	if (deleteBtn && existing) {
		deleteBtn.addEventListener("click", async () => {
			if (!confirm("Delete this observation?")) return;
			try {
				await api.deleteObservation(existing.id);
				navigate("");
			} catch (err) {
				const errEl = container.querySelector("#form-error")!;
				errEl.innerHTML = `<div class="error">${err instanceof Error ? err.message : "Delete failed"}</div>`;
			}
		});
	}
}

function esc(s: string): string {
	const el = document.createElement("span");
	el.textContent = s;
	return el.innerHTML;
}
